import { Box, Button, Chip, Grid, Typography } from "@mui/material";
import { settingsReportStyles } from "../../../styles/SettingsReport.styles";
import ClientPicker from "./ClientPicker";
import { PerformanceReportTypeEnum } from "helpers/types";
import AssetPicker from "./AssetPicker";
import DateSelector from "../Statistic/DateSelector";
import { useState, useCallback, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { useReportSettings } from "./ReportSettingsContext";
import { useSettingsReportValue } from "hooks/useReportStorage";
import ReportSettingsModal from "./ReportSettingsModal";
import CumulativeIncomeGlobalPeriodSetting from "./CumulativeIncomeGlobalPeriodSetting";
import StrategyTaxStateSetting from "./StrategyTaxStateSetting";
import ReportAccountHistory from "./ReportAccountHistory";
import {
  useGetAccountHistoryQuery,
  useListTaxRatesQuery,
} from "features/trading/accounts";
import { AdjustmentTypeEnum } from "helpers/types";
import { useNavigate } from "react-router-dom";

interface Props {
  type: PerformanceReportTypeEnum;
  header?: string;
  subtitle?: string;
}

type DateRange = [string | null, string | null];
const PERIOD_RANGES_KEY_PREFIX = "period_ranges";
const GLOBAL_CUMULATIVE_PERIOD_RANGES_KEY_PREFIX =
  "global_cumulative_period_ranges";

const getPeriodRangesKey = (type: PerformanceReportTypeEnum) =>
  `${PERIOD_RANGES_KEY_PREFIX}_${type}`;

const getGlobalCumulativePeriodRangesKey = (type: PerformanceReportTypeEnum) =>
  `${GLOBAL_CUMULATIVE_PERIOD_RANGES_KEY_PREFIX}_${type}`;

const parseDateRanges = (value: number | string): DateRange[] => {
  if (typeof value !== "string") return [];

  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];

    return parsed.filter(
      (range): range is DateRange =>
        Array.isArray(range) &&
        range.length === 2 &&
        (range[0] === null || typeof range[0] === "string") &&
        (range[1] === null || typeof range[1] === "string"),
    );
  } catch {
    return [];
  }
};

const getPeriodFromDateRanges = (ranges: DateRange[]): [string, string] => {
  const firstRange = ranges[0];
  if (!firstRange?.[0] || !firstRange?.[1]) return ["", ""];
  return [
    dayjs(firstRange[0]).format("YYYY-MM-DD"),
    dayjs(firstRange[1]).format("YYYY-MM-DD"),
  ];
};

const toOptionalPeriod = (
  period: [string, string],
): [string, string] | undefined => {
  const [startDate, endDate] = period;
  if (!startDate || !endDate) return undefined;
  return [startDate, endDate];
};

const sortAndUniqDateRanges = (ranges: DateRange[]): DateRange[] => {
  const deduped = ranges.filter(
    ([startDate, endDate], index, arr) =>
      arr.findIndex(([s, e]) => `${s}-${e}` === `${startDate}-${endDate}`) ===
      index,
  );
  return deduped.sort((a, b) => dayjs(a[0]).diff(dayjs(b[0])));
};

const SettingsReport = ({
  type,
  header = "Optic Equity Access Performance",
  subtitle = "OPTIC ASSET MANAGEMENT",
}: Props) => {
  const settingsPeriodRangesKey = getPeriodRangesKey(type);
  const globalCumulativePeriodRangesKey =
    getGlobalCumulativePeriodRangesKey(type);
  const [storedDateRanges, setStoredDateRanges] = useSettingsReportValue(
    settingsPeriodRangesKey,
    "[]",
  );
  const [
    storedGlobalCumulativeDateRanges,
    setStoredGlobalCumulativeDateRanges,
  ] = useSettingsReportValue(globalCumulativePeriodRangesKey, "[]");
  const [dateRanges, setDateRanges] = useState<DateRange[]>(() =>
    parseDateRanges(storedDateRanges),
  );
  const [globalCumulativeDateRanges, setGlobalCumulativeDateRanges] = useState<
    DateRange[]
  >(() => parseDateRanges(storedGlobalCumulativeDateRanges));
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const { updateSettings, settingsInfo } = useReportSettings();
  const navigate = useNavigate();
  const period = useMemo(
    () => getPeriodFromDateRanges(dateRanges),
    [dateRanges],
  );
  const globalCumulativePeriod = useMemo(
    () => getPeriodFromDateRanges(globalCumulativeDateRanges),
    [globalCumulativeDateRanges],
  );
  const taxRateFilters = useMemo(() => {
    if (settingsInfo.client_id) {
      return { client: settingsInfo.client_id };
    }
    return {};
  }, [settingsInfo.client_id]);
  const { data: taxRatesData } = useListTaxRatesQuery(
    {
      pagination: { page: 0, pageSize: 100 },
      sorting: [{ field: "id", sort: "desc" }],
      filters: taxRateFilters,
    },
    {
      skip: !settingsInfo.client_id,
    },
  );

  const inceptionAccountId = settingsInfo.account_id ?? null;
  const inceptionStrategyId = settingsInfo.strategy_id ?? null;
  const { data: inceptionHistory } = useGetAccountHistoryQuery(
    {
      account_id: inceptionAccountId!,
      strategy_id: inceptionStrategyId!,
      ordering: "adjustment_date",
      adjustment_type: AdjustmentTypeEnum.INCEPTION,
    },
    { skip: !inceptionAccountId || !inceptionStrategyId },
  );
  const inceptionDate = inceptionHistory?.[0]?.adjustment_date ?? null;
  const { data: removedHistory } = useGetAccountHistoryQuery(
    {
      account_id: inceptionAccountId!,
      strategy_id: inceptionStrategyId!,
      ordering: "-adjustment_date",
      adjustment_type: AdjustmentTypeEnum.REMOVED,
    },
    { skip: !inceptionAccountId || !inceptionStrategyId },
  );
  const removedDate = removedHistory?.[0]?.adjustment_date ?? null;

  useEffect(() => {
    setDateRanges(parseDateRanges(storedDateRanges));
  }, [storedDateRanges]);

  useEffect(() => {
    setGlobalCumulativeDateRanges(
      parseDateRanges(storedGlobalCumulativeDateRanges),
    );
  }, [storedGlobalCumulativeDateRanges]);

  useEffect(() => {
    setStoredDateRanges(JSON.stringify(dateRanges));
  }, [dateRanges, setStoredDateRanges]);

  useEffect(() => {
    setStoredGlobalCumulativeDateRanges(
      JSON.stringify(globalCumulativeDateRanges),
    );
  }, [globalCumulativeDateRanges, setStoredGlobalCumulativeDateRanges]);

  useEffect(() => {
    updateSettings({ period });
  }, [period, updateSettings]);

  useEffect(() => {
    updateSettings({
      global_cumulative_period: toOptionalPeriod(globalCumulativePeriod),
    });
  }, [globalCumulativePeriod, updateSettings]);

  const handleDateChange = useCallback((range: DateRange) => {
    const [startDate, endDate] = range;
    if (startDate && endDate) {
      setDateRanges((prev) => sortAndUniqDateRanges([...prev, range]));
    }
  }, []);

  const handleRemoveDateRange = useCallback((index: number) => {
    setDateRanges((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleGoToClient = useCallback(() => {
    if (!settingsInfo.client_id) return;
    const params = new URLSearchParams();
    params.set("client_id", String(settingsInfo.client_id));
    if (settingsInfo.account_id)
      params.set("account_id", String(settingsInfo.account_id));
    if (settingsInfo.strategy_id)
      params.set("strategy_id", String(settingsInfo.strategy_id));
    navigate(`/clients?${params.toString()}`);
  }, [
    settingsInfo.client_id,
    settingsInfo.account_id,
    settingsInfo.strategy_id,
    navigate,
  ]);

  const handleGlobalCumulativeDateChange = useCallback((range: DateRange) => {
    const [startDate, endDate] = range;
    if (startDate && endDate) {
      setGlobalCumulativeDateRanges((prev) =>
        sortAndUniqDateRanges([...prev, range]),
      );
    }
  }, []);

  const handleRemoveGlobalCumulativeDateRange = useCallback((index: number) => {
    setGlobalCumulativeDateRanges((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const formatDateRange = (range: DateRange) => {
    const [startDate, endDate] = range;
    if (startDate && endDate) {
      return `${dayjs(startDate).format("MM/DD/YYYY")} - ${dayjs(endDate).format("MM/DD/YYYY")}`;
    }
    return "";
  };

  return (
    <Box sx={settingsReportStyles.container}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={7}>
          <Typography
            variant="h3"
            component="h1"
            sx={settingsReportStyles.mainTitle}
          >
            {header}
          </Typography>
          <Typography
            variant="body1"
            component="h2"
            sx={settingsReportStyles.subtitle}
          >
            {subtitle}
          </Typography>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box sx={settingsReportStyles.summaryBox}>
            <ClientPicker type={type} />

            <Box sx={settingsReportStyles.summaryItem}>
              <Typography component="span" sx={settingsReportStyles.label}>
                Period:
              </Typography>
              <DateSelector
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
                onDateChange={handleDateChange}
                inceptionDate={inceptionDate}
                removedDate={removedDate}
                additionalComponents={
                  dateRanges.length > 0 ? (
                    <Box sx={settingsReportStyles.periodChipContainer}>
                      {dateRanges.map((range, index) => (
                        <Chip
                          key={`${range[0]}-${range[1]}`}
                          label={formatDateRange(range)}
                          onDelete={() => handleRemoveDateRange(index)}
                          size="small"
                          sx={settingsReportStyles.periodChip}
                        />
                      ))}
                    </Box>
                  ) : null
                }
              />
            </Box>
            <AssetPicker type={type} />
            <Box sx={settingsReportStyles.summaryItem}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setIsSettingsModalOpen(true)}
              >
                Open Settings
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <ReportSettingsModal
        open={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
      >
        {type === PerformanceReportTypeEnum.STOCK && (
          <CumulativeIncomeGlobalPeriodSetting
            dateRanges={globalCumulativeDateRanges}
            onDateChange={handleGlobalCumulativeDateChange}
            onRemoveDateRange={handleRemoveGlobalCumulativeDateRange}
          />
        )}
        {type === PerformanceReportTypeEnum.STRATEGY && (
          <StrategyTaxStateSetting
            existingTaxRates={taxRatesData?.results ?? []}
          />
        )}
        <ReportAccountHistory
          accountId={settingsInfo.account_id ?? null}
          strategyId={settingsInfo.strategy_id ?? null}
        />
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={!settingsInfo.client_id}
            onClick={handleGoToClient}
          >
            Go to Client
          </Button>
        </Box>
      </ReportSettingsModal>
    </Box>
  );
};

export default SettingsReport;
